import { primaryButton } from "../../../components/ui/buttonStyles.js";
import { Ico } from "../../../components/icons/icons.jsx";
import { normalizeCalculatedField } from "../../../logic/services/reportBuilderSummary.js";
import { rbInputStyle, rbSectionHint } from "../reportBuilderTheme.js";
import { ReportQueryBuilder } from "../components/ReportQueryBuilder.jsx";

function FieldLogicBlock({ logic, index, total, fieldNames, onUpdate, onRemove, canRemove, c, A, dark }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 10,
        border: `1px solid ${c.divider}`,
        background: c.hover,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: c.sub }}>
          Logic {index + 1}{total > 1 ? ` · WHEN branch` : ""}
        </span>
        {canRemove ? (
          <button
            type="button"
            onClick={onRemove}
            style={{
              height: 26,
              padding: "0 8px",
              borderRadius: 6,
              border: `1px solid ${c.cardBdr}`,
              background: c.card,
              color: "#EF4444",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Remove
          </button>
        ) : null}
      </div>

      <ReportQueryBuilder
        title="WHEN"
        hint="Conditions that activate this branch"
        query={logic.query}
        onQueryChange={(query) => onUpdate({ query })}
        fieldNames={fieldNames}
        mode="field"
        c={c}
        A={A}
        dark={dark}
      />

      <label style={{ display: "block" }}>
        <span style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: c.sub, marginBottom: 6 }}>
          THEN expression
        </span>
        <input
          value={logic.then ?? ""}
          onChange={(e) => onUpdate({ then: e.target.value })}
          placeholder="distance_km / NULLIF(fuel_used, 0)"
          style={rbInputStyle(c, false, { mono: true })}
        />
      </label>
    </div>
  );
}

export function CalculatedFieldsStep({
  calculatedFields,
  fieldNames,
  onAdd,
  onUpdate,
  onRemove,
  onAddLogic,
  onUpdateLogic,
  onRemoveLogic,
  c,
  A,
  dark,
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <p style={{ ...rbSectionHint(c), margin: 0 }}>
        Each calculated column supports multiple WHEN/THEN logic blocks (stacked as SQL CASE). GROUP BY is configured in the next step and is not required here.
      </p>

      {calculatedFields.length === 0 ? (
        <div style={{ padding: "20px 16px", borderRadius: 10, border: `1px dashed ${c.cardBdr}`, fontSize: 12, color: c.sub, textAlign: "center" }}>
          No calculated fields yet.
        </div>
      ) : (
        calculatedFields.map((raw, index) => {
          const field = normalizeCalculatedField(raw);
          const logics = field.logics ?? [];
          return (
            <div
              key={field.id}
              style={{
                padding: 14,
                borderRadius: 12,
                border: `1px solid ${c.divider}`,
                background: c.card,
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: c.sub, width: 20 }}>{index + 1}</span>
                <input
                  value={field.name}
                  onChange={(e) => onUpdate(field.id, { name: e.target.value })}
                  placeholder="Column name (e.g. fuel_efficiency)"
                  style={{ ...rbInputStyle(c), flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => onRemove(field.id)}
                  aria-label="Remove calculated field"
                  style={{
                    width: 32,
                    height: 32,
                    border: `1px solid ${c.cardBdr}`,
                    borderRadius: 7,
                    background: c.hover,
                    color: "#EF4444",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  {Ico.x}
                </button>
              </div>

              {logics.map((logic, logicIndex) => (
                <FieldLogicBlock
                  key={logic.id}
                  logic={logic}
                  index={logicIndex}
                  total={logics.length}
                  fieldNames={fieldNames}
                  onUpdate={(patch) => onUpdateLogic(field.id, logic.id, patch)}
                  onRemove={() => onRemoveLogic(field.id, logic.id)}
                  canRemove={logics.length > 1}
                  c={c}
                  A={A}
                  dark={dark}
                />
              ))}

              <button
                type="button"
                onClick={() => onAddLogic(field.id)}
                style={{
                  ...primaryButton(A, { dark, height: 32, fontSize: 12 }),
                  alignSelf: "flex-start",
                }}
              >
                {Ico.plus}
                Add logic block
              </button>

              <label style={{ display: "block" }}>
                <span style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: c.sub, marginBottom: 6 }}>
                  ELSE expression (optional)
                </span>
                <input
                  value={field.elseExpression ?? ""}
                  onChange={(e) => onUpdate(field.id, { elseExpression: e.target.value })}
                  placeholder="NULL"
                  style={rbInputStyle(c, false, { mono: true })}
                />
              </label>
            </div>
          );
        })
      )}

      <button type="button" onClick={onAdd} style={primaryButton(A, { dark, height: 34, fontSize: 12 })}>
        {Ico.plus}
        Add calculated field
      </button>
    </div>
  );
}
